import "./UsersAndPermissionsForAccount.scss";

import { Alert, Button, Flex, Popup, Text, Tooltip } from "@fluentui/react-northstar";
import { app } from "@microsoft/teams-js";
import { TeamsUser } from "@teams/types";
import { ChannelAccountDTO } from "@teams/types/api";
import React, { useEffect, useMemo, useState } from "react";

import { useTeamsMembers } from "../../actions/useTeamsMembers";
import { Dropdown } from "../../components/Dropdown";
import { Identity } from "../../components/Identity";
import { PollyIcon } from "../../components/PollyIcon";
import { TeamsLoader } from "../../components/TeamsLoader";
import { UserDropdown } from "../../components/UserDropdown";
import { FetchError, isError } from "../../services/ChannelApi";
import { useTeamsContext } from "../../state/TeamsContextProvider";
import { useTheme } from "../../state/ThemeProvider";
import { isMobileClient } from "../../utils/clientHelper";
import { getPageContext } from "../../utils/teamsHelpers";
import { BoltAlert } from "./BoltAlert";

export type UserSubscriptionRole = "owner" | "user";

// If changed, this value also needs to be changed in the `subscriptionManagement.ts` core file
const MAX_OWNERS = 5;

export const UsersAndPermissionsForAccount: React.FC<{
  canOwn: boolean;
  owners: TeamsUser[];
  maxUsers?: number;
  allowAddUsers?: boolean;
  showUsers?: boolean;
  users?: TeamsUser[];
  onUpgradeRequest?: () => void;
  onAddOwner: (account: ChannelAccountDTO) => Promise<Error | void>;
  onRemoveOwner: (owner: TeamsUser) => Promise<Error | void>;
  onAddUser?: (account: ChannelAccountDTO) => Promise<Error | void>;
  onRemoveUser?: (user: TeamsUser) => Promise<Error | void>;
}> = ({
  canOwn,
  owners,
  showUsers,
  maxUsers,
  allowAddUsers,
  users,
  onAddOwner,
  onRemoveOwner,
  onAddUser,
  onRemoveUser,
  onUpgradeRequest,
}) => {
  const { teamsContext, hostTab } = useTeamsContext();
  const { loadTeamsMembers, isTeamsMembersLoading, teamMembers } = useTeamsMembers();
  const { themeName } = useTheme();
  const [userManagementMode, setUserManagementMode] = useState<UserSubscriptionRole>(
    canOwn ? "owner" : "user"
  );
  const [error, setError] = useState<string>();

  const isAgile = useMemo(() => getPageContext() === "/agile", []);

  const filteredUsers = useMemo(() => {
    const ownerSet = new Set(owners.map(owner => owner.aadObjectId || owner.teamsUserId));
    const filteredUsers = users?.filter(
      user => !ownerSet.has(user.aadObjectId) && !ownerSet.has(user.teamsUserId)
    );

    return filteredUsers;
  }, [owners, users]);
  const isPersonalPage = hostTab === "dashboard";

  useEffect(() => {
    if (teamsContext) {
      loadTeamsMembers(teamsContext);
    }
  }, [loadTeamsMembers, teamsContext]);

  return (
    <div
      className={`users-and-permissions-account users-and-permissions-account__theme-${themeName}`}
    >
      <Flex vAlign="center" className="header">
        <Flex column gap="gap.smaller">
          <Text content="License management" size="large" weight="semibold" />
          <Text content="Users that are able to manage billing and creator licenses" />
        </Flex>
        {!isAgile && (
          <Flex.Item push>
            <div>
              {canOwn &&
              allowAddUsers &&
              !isMobileClient(teamsContext) &&
              (maxUsers === undefined || users === undefined || users.length < maxUsers) ? (
                <Button
                  primary
                  icon={<PollyIcon icon="plus-outlined" inheritColor />}
                  content="Get more licenses"
                  onClick={onUpgradeRequest}
                />
              ) : (
                <>
                  {maxUsers && (
                    <div className="users-and-permissions-account__user-add__license-quantity">
                      <Text>{`${users?.length}/${maxUsers} Licenses used`}</Text>
                    </div>
                  )}
                </>
              )}
            </div>
          </Flex.Item>
        )}
      </Flex>

      {canOwn && (
        <>
          {!isPersonalPage &&
            allowAddUsers &&
            (maxUsers === undefined || users === undefined || users.length < maxUsers) && (
              <TeamsLoader
                isLoading={isTeamsMembersLoading}
                loadingMessage="Loading Teams Users ..."
                size="small"
                labelPosition="end"
              >
                <Flex className="users-and-permissions-account__user-add">
                  <AddUserSelection
                    members={teamMembers.filter(
                      m => !users?.some(u => u.aadObjectId === m.aadObjectId)
                    )}
                    teamsContext={teamsContext as app.Context}
                    addButtonContent="Add User"
                    disabled={
                      maxUsers !== undefined && users !== undefined && users.length >= maxUsers
                    }
                    onAddUser={userManagementMode === "owner" ? onAddOwner : onAddUser!}
                    role={userManagementMode}
                    setActiveRole={setUserManagementMode}
                    canAddOwners={owners.length < MAX_OWNERS}
                  />
                  {!isAgile && maxUsers && (
                    <Flex.Item push>
                      <div className="users-and-permissions-account__user-add__license-quantity">
                        <Text>{`${users?.length}/${maxUsers} Licenses used`}</Text>
                      </div>
                    </Flex.Item>
                  )}
                </Flex>
              </TeamsLoader>
            )}
          {isPersonalPage && <PersonalPageAddUserWarning />}
        </>
      )}

      <Flex column hAlign="stretch">
        {error && <Alert content={error} danger />}
        {owners
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(owner => (
            <UserRow
              key={owner.aadObjectId}
              user={owner}
              showRemove={canOwn}
              allowRemove={canOwn && owners.length > 1}
              showChat={canOwn}
              removeWarning={
                owner.aadObjectId !== teamsContext?.user?.id ? (
                  <>
                    Are you sure you wish to remove <b>{owner.name}</b> as an owner?
                  </>
                ) : (
                  <>
                    Are you sure you want to remove <b>yourself</b> as an owner? You will no longer
                    be able to manage user licenses.
                  </>
                )
              }
              onRemove={async user => {
                await onRemoveOwner(user);
                await onRemoveUser?.(user);
              }}
              role="owner"
              onSwitchRoles={async () => {
                const result = await onRemoveOwner(owner);

                if (isError(result)) {
                  let message = result.message;

                  if (result instanceof FetchError) {
                    if (result.body?.message) {
                      message = result.body.message as string;
                    }
                  }
                  setError(`${message ?? "There was an error switching user roles."}`);
                } else {
                  setError(undefined);
                }

                return result;
              }}
              isAgile={isAgile}
            />
          ))}
        {showUsers &&
          filteredUsers &&
          filteredUsers
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(user => (
              <UserRow
                key={user.aadObjectId}
                user={user}
                showRemove={canOwn}
                allowRemove={!owners.some(owner => owner.aadObjectId === user.aadObjectId)}
                removeWarning={
                  <>
                    Are you sure you wish to remove <b>{user.name}</b>? This will disable all items
                    they own that use this subscription!
                  </>
                }
                onRemove={onRemoveUser}
                role="user"
                onSwitchRoles={async () => {
                  const result = await onAddOwner({
                    id: user.teamsUserId,
                    userPrincipalName: user.upn!,
                    name: user.name,
                    aadObjectId: user.aadObjectId,
                  } as ChannelAccountDTO);

                  if (isError(result)) {
                    setError(`There was an error switching user roles. ${result.message ?? ""}`);
                  } else {
                    setError(undefined);
                  }

                  return result;
                }}
                isAgile={isAgile}
                showChat={canOwn}
              />
            ))}
      </Flex>

      {!isAgile &&
        allowAddUsers &&
        (maxUsers === undefined || users === undefined || users.length >= maxUsers) && (
          <BoltAlert
            showButton={!isMobileClient(teamsContext) && canOwn}
            buttonCta="Upgrade"
            prompt="In order to add more members you'll need more licenses. Please upgrade your subscription to get more licenses"
            {...(canOwn &&
              onUpgradeRequest && {
                onButtonClick: onUpgradeRequest,
              })}
          />
        )}
    </div>
  );
};

export const UserRoleBadge: React.FC<{
  user?: TeamsUser;
  role: UserSubscriptionRole;
  onSwitchRoles?: () => Promise<void | Error>;
  badgeOnly?: boolean;
}> = ({ role, onSwitchRoles, user, badgeOnly }) => {
  const [confirmSwitchRole, setConfirmSwitchRole] = useState(false);
  const { teamsContext } = useTeamsContext();
  const getRoleFromLabel = (label: string) => {
    switch (label) {
      case "Owner":
        return "owner";
      case "Creator":
        return "user";
      default:
        return "user";
    }
  };

  const getLabelFromRole = (label: string) => {
    switch (label) {
      case "owner":
        return "Owner";
      case "user":
        return "Creator";
      default:
        return "Creator";
    }
  };

  const [selectedRole, setSelectedRole] = useState(role);
  const badgeText = useMemo(() => getLabelFromRole(selectedRole), [selectedRole]);

  return (
    <>
      {badgeOnly ? (
        <Flex
          className={`user-role-badge user-role-badge__${selectedRole} user-role-badge__${
            badgeOnly ? "badge-only" : ""
          }`}
        >
          {getLabelFromRole(role)}
        </Flex>
      ) : (
        <Popup
          open={confirmSwitchRole}
          onOpenChange={(_e, props) => setConfirmSwitchRole(!!props?.open)}
          content={
            <Flex column style={{ maxWidth: "300px" }} gap="gap.small">
              <Alert
                content="Are you sure you want to switch your role to creator? You will no longer have access to make changes to this subscription"
                warning
              />
              <Button
                data-tname="confirm-remove-user"
                content="Confirm Switch"
                fluid
                primary
                onClick={async () => {
                  const result = await onSwitchRoles?.();

                  if (isError(result)) {
                    setSelectedRole(role);
                  }
                }}
              />
            </Flex>
          }
        >
          <Dropdown
            items={["Owner", "Creator"]}
            isItemSelected={() => true}
            className={`user-role-badge user-role-badge__${selectedRole}`}
            onChange={async (_, props) => {
              const newRole = getRoleFromLabel(props.value as string);

              if (role === "owner" && user?.aadObjectId === teamsContext?.user?.id) {
                setConfirmSwitchRole(true);
                setSelectedRole(role);
                return;
              } else {
                const result = await onSwitchRoles?.();

                if (!isError(result)) {
                  setSelectedRole(newRole);
                } else {
                  setSelectedRole(role);
                }
              }
            }}
            value={getLabelFromRole(role)}
          >
            {badgeText}
          </Dropdown>
        </Popup>
      )}
    </>
  );
};

type UserRowProps = {
  user: TeamsUser;
  allowRemove: boolean;
  showRemove: boolean;
  removeWarning: React.ReactNode;
  onSwitchRoles: () => Promise<void | Error>;
  onRemove?: (user: TeamsUser) => Promise<void | Error>;
  role: UserSubscriptionRole;
  isAgile?: boolean;
  showChat: boolean;
};

const UserRow: React.FC<UserRowProps> = ({
  user,
  allowRemove,
  showRemove,
  onRemove,
  removeWarning,
  role,
  onSwitchRoles,
  isAgile,
  showChat,
}) => {
  const [confirmDelete, setConfirmDelete] = useState<boolean>();
  const [isRemoving, setIsRemoving] = useState<boolean>();
  const [removeError, setRemoveError] = useState<string>();

  return (
    <Flex className="users-and-permissions-account__user-row">
      <Flex.Item grow={1}>
        <Flex gap="gap.large" vAlign="center">
          <div className="users-and-permissions-account__user-row__identity">
            <Identity name={user.name} subname={user.upn} />
          </div>
          <UserRoleBadge
            role={role}
            onSwitchRoles={onSwitchRoles}
            user={user}
            badgeOnly={isAgile || !showRemove || !allowRemove}
          />
        </Flex>
      </Flex.Item>

      <Flex.Item push>
        <Flex gap="gap.medium" className="users-and-permissions-account__user-row__button">
          {showChat && (
            <Button
              data-tname="open-chat-with-user"
              icon={<PollyIcon icon="message-icon" inheritColor />}
              iconOnly
              disabled={!user.upn}
              text
              onClick={() => {
                app.openLink(`https://teams.microsoft.com/l/chat/0/0?users=${user.upn}`);
              }}
            />
          )}

          {showRemove && onRemove && (
            <Tooltip content="Remove user">
              <Popup
                open={confirmDelete}
                onOpenChange={(_e, props) => setConfirmDelete(props?.open)}
                content={
                  <Flex column style={{ maxWidth: "300px" }} gap="gap.small">
                    <Alert content={removeWarning} warning />
                    <Button
                      data-tname="confirm-remove-user"
                      content="Confirm Remove"
                      fluid
                      primary
                      disabled={isRemoving}
                      loading={isRemoving}
                      onClick={async () => {
                        setRemoveError(undefined);
                        setIsRemoving(true);
                        const result = await onRemove(user);
                        setIsRemoving(false);
                        if (isError(result)) {
                          setRemoveError(result.message);
                        } else {
                          setConfirmDelete(false);
                        }
                      }}
                    />
                    {removeError && <Alert danger content={removeError} attached="bottom" />}
                  </Flex>
                }
              >
                <Button
                  className="trash-icon-button"
                  data-tname="remove-user"
                  iconOnly
                  text
                  disabled={!allowRemove || isRemoving}
                  loading={isRemoving}
                  icon={<PollyIcon icon="trash-icon" inheritColor />}
                  onClick={() => setConfirmDelete(true)}
                />
              </Popup>
            </Tooltip>
          )}
        </Flex>
      </Flex.Item>
    </Flex>
  );
};

type AddUserSelectionProps = {
  members: ChannelAccountDTO[];
  teamsContext: app.Context;
  addButtonContent: React.ReactNode;
  disabled?: boolean;
  onAddUser: (account: ChannelAccountDTO) => Promise<void | Error>;
  role: UserSubscriptionRole;
  setActiveRole: (role: UserSubscriptionRole) => void;
  canAddOwners?: boolean;
};

const AddUserSelection: React.FC<AddUserSelectionProps> = ({
  teamsContext,
  members,
  onAddUser,
  disabled,
  addButtonContent,
  role,
  setActiveRole,
  canAddOwners,
}) => {
  const [selectedAddUser, setSelectedAddUser] = useState<ChannelAccountDTO>();
  const [isAdding, setIsAdding] = useState<boolean>();
  const [addError, setAddError] = useState<string>();
  const { themeName } = useTheme();

  const roleDropDownItems = [
    {
      key: "user",
      header: "Creator",
      value: "user",
    },
  ];

  if (canAddOwners) {
    roleDropDownItems.push({
      key: "owner",
      header: "Owner",
      value: "owner",
    });
  }

  return (
    <>
      <Flex vAlign="center" gap="gap.small">
        <Flex column gap="gap.smaller">
          <Flex gap="gap.smaller" vAlign="end" style={{ marginLeft: "2px" }}>
            <Text content="Add users" size="small" />
            <Tooltip
              content={`Only users that are members of the current team ${
                teamsContext.team?.displayName ? `(${teamsContext.team?.displayName})` : ""
              } are listed`}
              trigger={
                <span style={{ height: "16px" }}>
                  <PollyIcon icon="info-icon" size="small" />
                </span>
              }
            />
          </Flex>
          <UserDropdown
            users={members.map(m => ({ id: m.aadObjectId, name: m.name }))}
            value={selectedAddUser?.aadObjectId}
            onSelected={s => {
              const selectedUser = members?.find(u => u.aadObjectId === s);
              setSelectedAddUser(selectedUser);
              if (addError) {
                setAddError(undefined);
              }
            }}
          />
        </Flex>
        <Flex column gap="gap.smaller" vAlign="end">
          <Text content="Role" size="small" style={{ marginLeft: "2px" }} />
          <Dropdown
            items={roleDropDownItems}
            onChange={(_, props) => {
              setActiveRole(props.value as UserSubscriptionRole);
            }}
            isItemSelected={item => {
              return item.value === role;
            }}
            variables={{
              backgroundColor: "var(--stardustui-colorScheme-default-backgroundFocus)",
            }}
            defaultValue={role === "owner" && canAddOwners ? "Owner" : "Creator"}
            className="role-dropdown"
            fluid
            disabled={!selectedAddUser || isAdding || disabled}
          />
        </Flex>
        <Flex.Item align="end">
          <Button
            data-tname={addButtonContent?.toString().replace(" ", "-")}
            disabled={!selectedAddUser || isAdding || disabled}
            loading={isAdding}
            tinted
            icon={
              <PollyIcon
                icon="create-icon"
                inheritColor
                disabled={!selectedAddUser || isAdding || disabled}
              />
            }
            content={addButtonContent}
            onClick={async () => {
              if (!selectedAddUser) {
                return;
              }

              setAddError(undefined);

              setIsAdding(true);
              const result = await onAddUser(selectedAddUser);
              setIsAdding(false);

              if (isError(result)) {
                let message = result.message;

                if (result instanceof FetchError) {
                  if (result.body?.message) {
                    message = result.body.message as string;
                  }
                }
                setAddError(`${message ?? "There was an error adding the user."}`);
              }
            }}
            className={`tinted-button ${themeName}`}
          />
        </Flex.Item>

        {addError && (
          <Flex.Item align="end">
            <Alert danger attached="bottom" content={addError} />
          </Flex.Item>
        )}
      </Flex>
    </>
  );
};

const PersonalPageAddUserWarning: React.FC = () => {
  return (
    <Text
      content="To add owners, please access this polly via the Polly tab within the channel, chat or meeting it was created in."
      color="red"
      weight="semibold"
      as="h3"
      className="users-and-permissions-account__header"
    />
  );
};
